import React, { useEffect, useRef, useState } from "react";
import NavbarNew from "./NavbarNew";
import Sidenav from "./Sidenav";
import * as auth from "../../backend/auth";
import { toast } from "react-toastify";
import 'react-toastify/dist/ReactToastify.css';
import './Toaster.css';
import "./Layout.css"
import { useIdleTimer } from "react-idle-timer";
import { navigate } from "raviger";
import { getBasePath } from "../../App";
import SideNavNew from "./SidenavNew";
import { SidenavProvider, useSidenav } from "../../contexts/SidenavContext";
import { AssociateCompanyProvider } from "../../contexts/AssociateCompanyContext";

function AuthProtector(props: any) {
    const [authToken, setAuthToken] = useState(auth.getAuthToken());

    const { logout: performLogout } = auth.useLogout(
        () => {
            // Land on /login explicitly (not just '/') so an idle-timeout logout always leaves
            // the URL matching what's on screen, instead of showing the login form at whatever
            // path the user happened to be idle on.
            navigate(`${getBasePath()}/login`, { replace: true });
            window.location.reload();
        },
        (error: any) => {
            console.error('Auto-logout failed:', error);
            auth.clearUserSession();
            navigate(`${getBasePath()}/login`, { replace: true });
            window.location.reload();
        }
    );

    // Purely client-side "get this tab out safely" — clears this tab's own session
    // bookkeeping and redirects, WITHOUT calling the real backend logout endpoint. Used
    // whenever this tab's own token is gone/untrustworthy for a reason that isn't itself a
    // deliberate action by THIS tab (see staleSessionExit below for why that distinction
    // matters).
    function staleSessionExit() {
        auth.clearUserSession();
        toast.error("You have been logged out. Please login again.");
        // No window.location.reload() here, deliberately — unlike the idle-timeout/explicit
        // logout paths below, which reload to get a clean slate for the NEXT login. A reload
        // tears down the whole page (ToastContainer included) no matter how long you wait
        // first, so it would silently swallow the toast above every single time — same reason
        // request.ts's forceReLogin (whose toast reliably shows) only ever does a plain
        // client-side navigate(), never a reload. AuthProtector's own `authToken ? children :
        // null` already stops rendering the stale authenticated page immediately, so nothing
        // further needs a hard reload to be cleared away.
        navigate(`${getBasePath()}/login`, { replace: true });
    }

    function logout() {
        // The idle timer firing here means THIS tab has been sitting untouched — it's a
        // deliberate, real end to its own session, so it's correct (and expected) to call the
        // real logout API. But if this tab's own token has ALREADY gone stale/foreign because
        // of something a DIFFERENT tab did (cross-tab logout/re-login while this one sat idle
        // — exactly the case the resync effect below exists for), calling performLogout() here
        // would hit POST /api/v1/auth/logout using whatever token/cookies are currently live in
        // this browser — which, by then, may belong to a completely different, unrelated tab's
        // freshly-established session — and end THAT session server-side instead of this dead
        // one. Route through the safe, network-free exit in that case.
        if (!auth.getAuthToken() || auth.isSessionInvalidated()) {
            staleSessionExit();
            return;
        }
        performLogout();
    }

    useIdleTimer({
        onIdle: () => logout(),
        timeout: 480 * 60 * 1000, // In milliseconds,
        events: [
            'keydown',
            'mousedown'
        ]
    })

    useEffect(() => {
        if (!authToken) {
            // This effect only ever reacts to authToken going FALSY — i.e. the shared token
            // disappearing entirely (a genuinely-shared session logged out elsewhere), never to
            // it merely changing to a different, still-valid token (a different tab logging in
            // as someone else) — see the resync effect below, which leaves a truthy swap alone
            // for request.ts's stale-tab gate to catch on this tab's next real action instead.
            // Even so: do NOT call the real logout API from here. This fires purely because of
            // an external (cross-tab) change, not a deliberate action by this tab, and by the
            // time it runs the browser's shared cookies/whatever's briefly live may not be this
            // tab's own anymore — same reasoning as logout() above. Exit client-side only.
            staleSessionExit();
        }
    }, [authToken]);

    // authToken above was only ever read once, at mount. If this tab's session dies without it
    // ever making another API call — e.g. a genuinely-shared session (two tabs on the exact
    // same login) gets logged out from the OTHER tab, so nothing here trips the axios
    // interceptor's stale-session gate — this tab kept rendering its authenticated page against
    // a token that no longer exists, and whatever it tried to read next would throw with no
    // error boundary to catch it (the "blue screen"). Resync from the live shared token whenever
    // something could have changed it so the guard above actually re-fires and routes this tab
    // through the same clean staleSessionExit() toast+redirect, instead of crashing.
    useEffect(() => {
        function resync() {
            setAuthToken(auth.getAuthToken());
        }
        window.addEventListener('storage', resync);
        window.addEventListener('focus', resync);
        return () => {
            window.removeEventListener('storage', resync);
            window.removeEventListener('focus', resync);
        };
    }, []);

    return (
        <>
            {
                authToken ? props.children : null
            }
        </>
    )
}

function LayoutWithSideNavContent(props: any) {
    const { open, toggleSidenav } = useSidenav();
    const sidenavRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        if (!open) return;
        function onMouseMove(e: MouseEvent) {
            if (!sidenavRef.current) return;
            const rect = sidenavRef.current.getBoundingClientRect();
            if (e.clientX < rect.left || e.clientX > rect.right ||
                e.clientY < rect.top  || e.clientY > rect.bottom) {
                toggleSidenav(false);
            }
        }
        document.addEventListener('mousemove', onMouseMove);
        return () => document.removeEventListener('mousemove', onMouseMove);
    }, [open, toggleSidenav]);

    return (
        <AuthProtector>
            <div className="main-conatiner" >
                <NavbarNew />
                < div className="page-layout-container" >
                    <div
                        ref={sidenavRef}
                        className={`sidenav-container d-flex flex-column justify-content-between ${open ? 'open' : ''}`}
                        onMouseEnter={() => toggleSidenav(true)}
                    >
                        <SideNavNew open={open} toggleSidenav={toggleSidenav} />
                    </div>
                    < div className="main-container" >
                        {props.children}
                    </div>
                </div>
            </div>
        </AuthProtector>
    )
}

function LayoutWithSideNav(props: any) {
    return (
        <SidenavProvider>
            <AssociateCompanyProvider>
                <LayoutWithSideNavContent {...props} />
            </AssociateCompanyProvider>
        </SidenavProvider>
    );
}

export default LayoutWithSideNav;